// Customer-facing AI Credits page. Shows balance, lets them top up, and
// configures auto-topup. Generation routes throw 402 when balance < cost,
// and the UI catches that to bring the user here.
import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Field, Input, Select, Spinner } from '../ui.jsx';

const TOPUP_PRESETS = [25, 50, 100, 250];

export function AiCredits({ user, toast }) {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(50);
  const [busy, setBusy] = useState(false);

  const load = () => api('/api/billing/credits').then(setData).catch(()=>{});
  useEffect(() => { load(); }, []);

  const topup = async () => {
    if (!amount || amount < 5) return toast('Minimum top-up is $5', 'error');
    setBusy(true);
    try {
      const r = await api('/api/billing/credits/topup', { method:'POST', body:{ amountUsd: amount }});
      if (r.dev) {
        toast(`✓ Dev top-up · new balance $${(r.newBalanceCents/100).toFixed(2)}`);
        load();
        return;
      }
      if (r.clientSecret) {
        toast('Opening secure payment…');
        // Lazy-load Stripe.js
        if (!window.Stripe) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script'); s.src = 'https://js.stripe.com/v3'; s.onload = resolve; s.onerror = reject; document.head.appendChild(s);
          });
        }
        const pkRes = await api('/api/billing/public-key').catch(()=>({}));
        const stripe = window.Stripe(pkRes.publishableKey || pkRes.key);
        const result = await stripe.confirmPayment({
          clientSecret: r.clientSecret,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });
        if (result.error) { toast(result.error.message, 'error'); return; }
        // Confirm with our server (webhook is canonical, this is safety net)
        await api('/api/billing/credits/confirm', { method:'POST', body:{ paymentIntentId: r.paymentIntentId }});
        toast(`✓ Added $${amount} credit`);
        load();
      }
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const saveAutoTopup = async (patch) => {
    try { await api('/api/billing/credits/auto-topup', { method:'PATCH', body: patch }); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  if (!data) return <div style={{ padding:40 }}><Spinner sz={32}/></div>;

  const low = data.balanceCents < 1000;

  return <div style={{ padding:24, maxWidth:880 }}>
    <h1 style={{ margin:'0 0 8px', fontSize:24, fontWeight:800 }}>💳 AI Credits</h1>
    <div style={{ color:G.textMuted, fontSize:14, marginBottom:20 }}>
      AI image, video, and voice generation runs on prepaid credit. No surprise bills — when balance runs out, generations pause until you top up.
    </div>

    {/* Balance card */}
    <div style={{ ...styles.card, padding:24, marginBottom:18, borderColor: low?G.yellow+'66':G.purple+'66', background: low?G.yellow+'11':G.purpleDim }}>
      <div style={{ fontSize:11, color:low?G.yellow:G.purple, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Current balance</div>
      <div style={{ fontSize:48, fontWeight:800, color: low?G.yellow:G.purple, margin:'4px 0' }}>{fmtUsd(data.balanceUsd)}</div>
      {low && <div style={{ fontSize:13, color:G.yellow, fontWeight:600 }}>⚠ Running low — top up to keep AI tools working</div>}
    </div>

    {/* Topup card */}
    <div style={{ ...styles.card, padding:20, marginBottom:18 }}>
      <h3 style={{ margin:'0 0 10px', fontSize:15 }}>➕ Add credit</h3>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
        {TOPUP_PRESETS.map(p => (
          <button key={p} onClick={()=>setAmount(p)} style={{
            padding:'10px 18px', borderRadius:10, border:`2px solid ${amount===p?G.purple:G.cardBorder}`,
            background: amount===p?G.purpleDim:'transparent', color: amount===p?G.purple:G.text,
            fontWeight:700, cursor:'pointer', fontSize:14,
          }}>${p}</button>
        ))}
        <Input type="number" value={amount} onChange={e=>setAmount(Number(e.target.value))} min={5} max={1000} step={5} style={{ width:110 }}/>
      </div>
      <Btn v="primary" onClick={topup} disabled={busy || !amount || amount < 5}>
        {busy ? <Spinner sz={14}/> : `Top up $${amount}`}
      </Btn>
      {!data.hasCard && <div style={{ marginTop:10, fontSize:12, color:G.textMuted }}>No card on file yet — we'll prompt for one during checkout.</div>}
    </div>

    {/* Auto-topup */}
    <div style={{ ...styles.card, padding:20, marginBottom:18 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <h3 style={{ margin:0, fontSize:15 }}>🔁 Auto-topup</h3>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={data.autoTopup.enabled} onChange={e=>saveAutoTopup({ enabled: e.target.checked })}/>
          {data.autoTopup.enabled ? <span style={{ color:G.green, fontWeight:700 }}>ON</span> : <span style={{ color:G.textMuted }}>OFF</span>}
        </label>
      </div>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:14 }}>
        Charge your saved card automatically when balance drops below the threshold. Generations never pause.
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <Field label="When balance drops below">
          <Input type="number" value={data.autoTopup.thresholdUsd} step={5} onChange={e=>saveAutoTopup({ thresholdUsd: Number(e.target.value) })} disabled={!data.autoTopup.enabled}/>
        </Field>
        <Field label="Top up by">
          <Input type="number" value={data.autoTopup.amountUsd} step={5} onChange={e=>saveAutoTopup({ amountUsd: Number(e.target.value) })} disabled={!data.autoTopup.enabled}/>
        </Field>
      </div>
    </div>

    {/* History */}
    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <div style={{ padding:'14px 18px', borderBottom:`1px solid ${G.border}` }}>
        <strong style={{ fontSize:14 }}>Recent activity</strong>
      </div>
      {data.ledger.length === 0 ? (
        <div style={{ padding:24, textAlign:'center', color:G.textMuted, fontSize:13 }}>No credit activity yet.</div>
      ) : (
        <table style={{ width:'100%', fontSize:13, borderCollapse:'collapse' }}>
          <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
            <th style={{ textAlign:'left', padding:'8px 14px' }}>When</th>
            <th style={{ textAlign:'left', padding:'8px 14px' }}>Kind</th>
            <th style={{ textAlign:'left', padding:'8px 14px' }}>Description</th>
            <th style={{ textAlign:'right', padding:'8px 14px' }}>Amount</th>
            <th style={{ textAlign:'right', padding:'8px 14px' }}>Balance</th>
          </tr></thead>
          <tbody>{data.ledger.map(l => (
            <tr key={l.id} style={{ borderTop:`1px solid ${G.border}` }}>
              <td style={{ padding:'8px 14px', color:G.textMuted, fontSize:12 }}>{new Date(l.created_at).toLocaleString()}</td>
              <td style={{ padding:'8px 14px' }}>{l.kind}</td>
              <td style={{ padding:'8px 14px', color:G.textMuted, fontSize:12 }}>{l.description || '—'}</td>
              <td style={{ padding:'8px 14px', textAlign:'right', color: l.amount_cents>=0?G.green:G.red, fontWeight:700 }}>
                {l.amount_cents>=0?'+':''}${(l.amount_cents/100).toFixed(2)}
              </td>
              <td style={{ padding:'8px 14px', textAlign:'right', fontFamily:'monospace' }}>${(l.balance_after_cents/100).toFixed(2)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  </div>;
}
